import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import "./DecadeNavBar.css";

export default function DecadeNavBarMobile({
  decades,
  activeDecade,
  onDecadeClick,
  showDecadeNav,
  compact = false,
}) {
  const btnRefs = useRef({});
  const containerRef = useRef(null);
  const activeDecadeRef = useRef(activeDecade);
  const scrollEndTimerRef = useRef(null);
  const releaseProgrammaticTimerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const hasMountedRef = useRef(false);
  const dragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });
  const suppressClickRef = useRef(false);
  const visibilityTimerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [wheelLayout, setWheelLayout] = useState({
    visibleCount: 5,
    itemWidth: 96,
    gap: 6,
  });
  const [navbarOffset, setNavbarOffset] = useState(62);
  const [visibilityPhase, setVisibilityPhase] = useState(() =>
    showDecadeNav === false ? "hidden" : "visible"
  );

  useEffect(() => {
    activeDecadeRef.current = activeDecade;
  }, [activeDecade]);

  useEffect(() => {
    const updateNavbarOffset = () => {
      const navbarEl = document.querySelector(".navbar");
      if (!navbarEl) return;
      const nextOffset = Math.max(
        0,
        Math.round(navbarEl.getBoundingClientRect().height) - 1
      );
      setNavbarOffset((prev) => (prev === nextOffset ? prev : nextOffset));
    };

    updateNavbarOffset();

    const navbarEl = document.querySelector(".navbar");
    let resizeObserver;
    if (navbarEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateNavbarOffset);
      resizeObserver.observe(navbarEl);
    }

    window.addEventListener("resize", updateNavbarOffset);
    window.addEventListener("orientationchange", updateNavbarOffset);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateNavbarOffset);
      window.removeEventListener("orientationchange", updateNavbarOffset);
    };
  }, []);

  useEffect(() => {
    if (visibilityTimerRef.current) {
      clearTimeout(visibilityTimerRef.current);
      visibilityTimerRef.current = null;
    }

    if (showDecadeNav === false) {
      setVisibilityPhase((prev) => {
        if (prev === "hidden" || prev === "exiting") return prev;
        return "exiting";
      });
      visibilityTimerRef.current = setTimeout(() => {
        setVisibilityPhase("hidden");
        visibilityTimerRef.current = null;
      }, 260);
      return;
    }

    setVisibilityPhase((prev) => {
      if (prev === "visible" || prev === "entering") return prev;
      return "entering";
    });
    visibilityTimerRef.current = setTimeout(() => {
      setVisibilityPhase("visible");
      visibilityTimerRef.current = null;
    }, 320);

    return () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
    };
  }, [showDecadeNav]);

  const centerDecade = useCallback((decade, behavior = "smooth") => {
    const container = containerRef.current;
    const btn = btnRefs.current[decade];
    if (!container || !btn) return;

    const targetLeft =
      btn.offsetLeft - (container.clientWidth - btn.offsetWidth) / 2;
    const clampedLeft = Math.max(0, targetLeft);

    isProgrammaticScrollRef.current = true;
    container.scrollTo({ left: clampedLeft, behavior });

    if (releaseProgrammaticTimerRef.current) {
      clearTimeout(releaseProgrammaticTimerRef.current);
    }
    releaseProgrammaticTimerRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, behavior === "smooth" ? 380 : 20);
  }, []);

  const findClosestDecade = useCallback(() => {
    const container = containerRef.current;
    if (!container || decades.length === 0) return null;

    const containerCenter = container.scrollLeft + container.offsetWidth / 2;
    let minDist = Infinity;
    let closest = null;

    decades.forEach((decade) => {
      const btn = btnRefs.current[decade];
      if (!btn) return;
      const btnCenter = btn.offsetLeft + btn.offsetWidth / 2;
      const dist = Math.abs(btnCenter - containerCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = decade;
      }
    });

    return closest;
  }, [decades]);

  const settleToClosestDecade = useCallback(() => {
    const closest = findClosestDecade();
    if (!closest) return;

    centerDecade(closest, "smooth");
    if (closest !== activeDecadeRef.current) {
      onDecadeClick(closest);
    }
  }, [centerDecade, findClosestDecade, onDecadeClick]);

  // 根据容器宽度自适应显示数量与按钮宽度，避免两端出现半个按钮
  const recomputeWheelLayout = useCallback(() => {
    const container = containerRef.current;
    const width = container?.clientWidth || window.innerWidth || 0;
    if (!width) return;

    const minItemWidth = 76;
    const gap = width <= 380 ? 4 : width <= 600 ? 6 : 8;
    const candidates = [9, 7, 5, 3, 1];

    let visibleCount = 1;
    for (const count of candidates) {
      if (count > decades.length) continue;
      const candidateWidth = (width - gap * (count - 1)) / count;
      if (candidateWidth >= minItemWidth) {
        visibleCount = count;
        break;
      }
    }

    if (visibleCount > decades.length) visibleCount = decades.length;
    if (visibleCount % 2 === 0 && visibleCount > 1) visibleCount -= 1;
    if (visibleCount < 1) visibleCount = 1;

    const itemWidth = Math.max(
      68,
      Math.floor((width - gap * (visibleCount - 1)) / visibleCount)
    );

    setWheelLayout((prev) => {
      if (
        prev.visibleCount === visibleCount &&
        prev.itemWidth === itemWidth &&
        prev.gap === gap
      ) {
        return prev;
      }
      return { visibleCount, itemWidth, gap };
    });
  }, [decades.length]);

  // activeDecade变化后，将对应按钮吸附到中间
  useEffect(() => {
    if (!activeDecade) return;
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      centerDecade(activeDecade, "auto");
      return;
    }
    centerDecade(activeDecade, "smooth");
    // decades用于确保数据更新后仍能正确定位
  }, [activeDecade, decades, centerDecade]);

  // 拖拽/滑动结束后再跳转对应年代
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      scrollEndTimerRef.current = setTimeout(() => {
        settleToClosestDecade();
      }, 140);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      if (releaseProgrammaticTimerRef.current) {
        clearTimeout(releaseProgrammaticTimerRef.current);
      }
    };
  }, [settleToClosestDecade]);

  useEffect(() => {
    recomputeWheelLayout();
    const container = containerRef.current;
    let resizeObserver;

    if (container && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        recomputeWheelLayout();
      });
      resizeObserver.observe(container);
    }

    window.addEventListener("resize", recomputeWheelLayout);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", recomputeWheelLayout);
    };
  }, [recomputeWheelLayout]);

  const handlePointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const container = containerRef.current;
    if (!container) return;
    dragStateRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: container.scrollLeft,
      moved: false,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (e) => {
    const container = containerRef.current;
    const dragState = dragStateRef.current;
    if (!container || !dragState.active) return;
    if (dragState.pointerId !== e.pointerId) return;
    if (e.pointerType !== "mouse") return;

    const deltaX = e.clientX - dragState.startX;
    if (Math.abs(deltaX) > 2) {
      dragState.moved = true;
    }
    container.scrollLeft = dragState.startScrollLeft - deltaX;
  };

  const handlePointerUp = (e) => {
    const dragState = dragStateRef.current;
    if (dragState.active && dragState.pointerId === e.pointerId && dragState.moved) {
      suppressClickRef.current = true;
    }
    dragStateRef.current.active = false;
    dragStateRef.current.pointerId = null;
    setIsDragging(false);
  };

  const handleDecadeClick = (decade) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    centerDecade(decade, "smooth");
    if (decade !== activeDecadeRef.current) {
      onDecadeClick(decade);
    }
  };

  const wheelTrackStyle = useMemo(
    () => ({
      "--wheel-gap": `${wheelLayout.gap}px`,
      "--wheel-item-width": `${wheelLayout.itemWidth}px`,
      "--wheel-edge-padding": `calc(50% - ${wheelLayout.itemWidth / 2}px)`,
    }),
    [wheelLayout]
  );

  return (
    <div
      className={`decade-navbar decade-wheel${compact ? " compact" : ""} mobile-visibility-${visibilityPhase}`}
      style={{ "--mobile-decade-top": `${navbarOffset}px` }}
    >
      <div
        ref={containerRef}
        className={`decade-wheel-track${isDragging ? " dragging" : ""}`}
        style={wheelTrackStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {decades.map((decade) => (
          <button
            key={decade}
            ref={(el) => (btnRefs.current[decade] = el)}
            className={`decade-btn decade-wheel-btn${
              activeDecade === decade ? " active" : ""
            }`}
            onClick={() => handleDecadeClick(decade)}
          >
            {decade}
          </button>
        ))}
      </div>
    </div>
  );
}
